import {
  AlertTriangle,
  Calendar,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import type React from "react";
import { useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { useUserPreferenceDefaults } from "@/contexts/UserPreferenceDefaultsContext";
import { useTerminology } from "@/hooks/useTerminology";
import { getCurrencySymbol } from "@/utils/currency";
import { ApiError } from "../../api/client";
import type { ForecastDataPointResponseApi } from "../../api/models/forecast";
import { useApp } from "../../contexts/AppContext";
import { BANK_ACCOUNTS } from "../../data/financial-systems";
import {
  generate12MonthData,
  monthKeyLabel,
  monthKeyOf,
} from "../../data/forecast12MonthDemoData";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useDrillDown } from "../../hooks/useDrillDown";
import { useForecast } from "../../hooks/useForecast";
import { useFormatting } from "../../hooks/useFormatting";
import { usePermissions } from "../../hooks/usePermissions";
import { monthBucketKey, parseDateOnly } from "../../lib/forecast-dates";
import { Button } from "../ui/button";
import { Card, CardContent, CardTitle } from "../ui/card";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import {
  CATEGORIES_VIEW,
  Forecast12MonthTable,
  type Forecast12MonthTableHandle,
  monthKeyFilter,
} from "./Forecast12MonthTable";

const CHART_LINE_COLOR = "var(--accent-sky-500)";
const CHART_THRESHOLD_COLOR = "var(--mc-error-icon)";

type ViewType = "line" | "bar" | "combined";

/** The subset of Recharts' mouse state the chart click handler reads. */
interface ChartMouseState {
  activeTooltipIndex?: number | string | null;
  activeIndex?: number | string | null;
}

/** The subset of Recharts' bar click payload the chart click handler reads. */
interface BarClickState {
  month?: string;
  payload?: { month?: string };
  activePayload?: { payload: { month?: string } }[];
}

interface TooltipProps {
  active?: boolean;
  label?: string;
  payload?: {
    name: string;
    value: number;
    color?: string;
    type?: string;
  }[];
}

/** One plotted month of the base 12-month forecast. */
interface MonthPoint {
  month: string;
  monthKey: string;
  balance: number;
  inflow: number;
  outflow: number;
  net: number;
}

function aggregateTimelineToMonths(
  timeline: ForecastDataPointResponseApi[],
): MonthPoint[] {
  const buckets = new Map<
    string,
    { monthKey: string; balance: number; inflow: number; outflow: number }
  >();

  for (const dp of timeline) {
    const key = monthBucketKey(dp.date);
    const existing = buckets.get(key);
    if (existing) {
      existing.inflow += dp.incoming;
      existing.outflow += dp.outgoing;
      existing.balance = dp.balance;
    } else {
      buckets.set(key, {
        monthKey: monthKeyOf(parseDateOnly(dp.date)),
        inflow: dp.incoming,
        outflow: dp.outgoing,
        balance: dp.balance,
      });
    }
  }

  return Array.from(buckets.entries()).map(([month, data]) => ({
    month,
    monthKey: data.monthKey,
    balance: data.balance,
    inflow: data.inflow,
    outflow: data.outflow,
    net: data.inflow - data.outflow,
  }));
}

export const Forecast12Month: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const { forecastNotReady } = useBaseForecast();
  const { mode, handleNavigate } = useApp();
  const { hasPermission } = usePermissions();
  const { getForecastTerm, getCashMovementTerms } = useTerminology();
  const { showDrillDown, closeDrillDown } = useDrillDown();
  const isLive = mode === "live";
  const {
    data: forecastResponse,
    isLoading: isForecastLoading,
    error: forecastError,
  } = useForecast("12m");
  const [viewType, setViewType] = useState<ViewType>("combined");

  const { defaults: chartDefaults } = useUserPreferenceDefaults();
  const threshold = chartDefaults.minCashThreshold;
  const { in: incomingTerm, out: outgoingTerm } = getCashMovementTerms();

  // The on-page table is driven imperatively from the chart's "View Detail"
  // action, which navigates it into the drilled Category Balances view.
  const tableRef = useRef<Forecast12MonthTableHandle>(null);

  const baseData: MonthPoint[] =
    isLive && forecastResponse
      ? aggregateTimelineToMonths(forecastResponse.timeline)
      : generate12MonthData();

  // Clearing the view history on a third-party reset: a new forecast run or a
  // demo/live switch invalidates the visited trail.
  const historyResetKey = `${mode}-${forecastResponse?.forecastRunId ?? "none"}`;

  const startingCash =
    isLive && forecastResponse
      ? forecastResponse.metrics.startingCash
      : BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);

  const openingBalanceFor = (index: number) =>
    index === 0 ? startingCash : baseData[index - 1].balance;

  // Chart click → shared drill-down summary modal for that month, whose single
  // action drives the table into the drilled Category Balances view.
  const activeChartIdxRef = useRef<number | null>(null);
  const handleChartMouseMove = (state: ChartMouseState) => {
    const raw = state?.activeTooltipIndex ?? state?.activeIndex;
    activeChartIdxRef.current = raw != null ? Number(raw) : null;
  };

  const openMonthSummary = (index: number) => {
    const point = baseData[index];
    if (!point) return;
    const opening = openingBalanceFor(index);
    showDrillDown("forecast-point", {
      type: "forecast-point",
      title: `${monthKeyLabel(point.monthKey)} — Cash Flow Summary`,
      mainValue: point.balance,
      breakdown: [
        { label: "Opening Balance", value: opening, emphasis: "normal" },
        {
          label: `Total ${incomingTerm}`,
          value: point.inflow,
          emphasis: "normal",
        },
        {
          label: `Total ${outgoingTerm}`,
          value: -point.outflow,
          emphasis: "normal",
        },
        {
          label: "Closing Balance",
          value: point.balance,
          emphasis: "total",
          rule: "single",
        },
        {
          label: "Net Change",
          value: point.net,
          emphasis: "total",
          rule: "double",
        },
      ],
      actions: [
        {
          label: "View Detail",
          variant: "primary",
          onClick: () => {
            closeDrillDown();
            tableRef.current?.navigateTo(
              CATEGORIES_VIEW,
              monthKeyFilter(point.monthKey),
            );
          },
        },
      ],
    });
  };

  const handleChartClick = () => {
    const idx = activeChartIdxRef.current;
    if (idx == null || Number.isNaN(idx)) return;
    openMonthSummary(idx);
  };

  const handleBarClick = (data: unknown) => {
    const state = data as BarClickState;
    const month =
      state?.activePayload?.[0]?.payload?.month ??
      state?.payload?.month ??
      state?.month;
    if (!month) return;
    openMonthSummary(baseData.findIndex((row) => row.month === month));
  };

  const lowestBalance = Math.min(...baseData.map((d) => d.balance));
  const highestBalance = Math.max(...baseData.map((d) => d.balance));
  const averageBalance =
    baseData.reduce((sum, d) => sum + d.balance, 0) / baseData.length;
  const totalInflow = baseData.reduce((sum, d) => sum + d.inflow, 0);
  const totalOutflow = baseData.reduce((sum, d) => sum + d.outflow, 0);
  const netCashFlow = totalInflow - totalOutflow;
  const lowestMonth =
    baseData.find((d) => d.balance === lowestBalance)?.month ?? "N/A";
  const highestMonth =
    baseData.find((d) => d.balance === highestBalance)?.month ?? "N/A";

  // Recovery detection: find the last month at the minimum balance (real turning point)
  const lowestIndex = baseData.reduce(
    (lastIdx, d, i) => (d.balance <= lowestBalance ? i : lastIdx),
    -1,
  );
  const troughMonth = baseData[lowestIndex]?.month ?? "N/A";
  const recoveryMargin = averageBalance * 0.05;
  const hasRecovery =
    lowestIndex >= 0 &&
    lowestIndex < baseData.length - 1 &&
    baseData
      .slice(lowestIndex + 1)
      .some((d) => d.balance > lowestBalance + recoveryMargin);
  const tailDeclining =
    !hasRecovery &&
    baseData.length > 1 &&
    baseData[baseData.length - 1].balance <
      baseData[Math.max(0, lowestIndex)].balance;

  // Find the exact peak month before the trough for the decline callout
  const declineStartIndex = baseData
    .slice(0, lowestIndex + 1)
    .reduce(
      (peakIdx, d, i, arr) => (d.balance > arr[peakIdx].balance ? i : peakIdx),
      0,
    );
  const declineStartMonth = baseData[declineStartIndex]?.month ?? "N/A";

  if (!hasPermission("view_12month_forecast_screen")) return null;

  const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
    if (active && payload?.length) {
      return (
        <div className="bg-popover text-popover-foreground border shadow-md rounded-md px-3 py-2 text-sm">
          <p className="font-semibold mb-1.5">{label}</p>
          {payload
            .filter((entry) => entry.type !== "none")
            .map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground capitalize">
                  {entry.name}:
                </span>
                <span className="font-semibold ml-auto pl-3">
                  {formatCurrency(entry.value)}
                </span>
              </div>
            ))}
        </div>
      );
    }
    return null;
  };

  if (isLive && isForecastLoading) {
    return (
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">
                Loading 12-month {getForecastTerm(false)} data...
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    isLive &&
    (forecastNotReady ||
      (forecastError instanceof ApiError && forecastError.status === 404))
  ) {
    return (
      <div className="p-6">
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <TrendingUp className="h-12 w-12 text-muted-foreground/40 mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              No {getForecastTerm(false)} data available
            </h3>
            <p className="text-muted-foreground text-sm max-w-md mb-6">
              A {getForecastTerm(false)} refresh has not been run yet. Run a{" "}
              {getForecastTerm(false)} refresh to populate this screen with cash
              position data and projections.
            </p>
            {hasPermission("trigger_forecast_refresh") && (
              <Button onClick={() => handleNavigate("forecast-refresh")}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Go to Refresh {getForecastTerm(true)}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLive && forecastError) {
    return (
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-destructive py-12">
              {forecastError instanceof Error
                ? forecastError.message
                : "Failed to load 12-month forecast"}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Lowest Balance */}
        <Card
          className="h-full"
          style={{
            borderLeft: `3px solid ${lowestBalance < threshold ? "var(--mc-error-border)" : "var(--mc-warning-border)"}`,
          }}
        >
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                Lowest Balance
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor:
                    lowestBalance < threshold
                      ? "var(--mc-error-bg)"
                      : "var(--mc-warning-bg)",
                }}
              >
                <AlertTriangle
                  className="h-4 w-4"
                  style={{
                    color:
                      lowestBalance < threshold
                        ? "var(--mc-error-icon)"
                        : "var(--mc-warning-icon)",
                  }}
                />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(lowestBalance)}
              </div>
              <div className="text-sm text-neutral-800 mt-1">
                {lowestBalance < threshold
                  ? `⚠ Below threshold in ${lowestMonth}`
                  : lowestMonth}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Highest Balance */}
        <Card
          className="h-full"
          style={{ borderLeft: "3px solid var(--mc-info-border)" }}
        >
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                Highest Balance
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "var(--mc-info-bg)" }}
              >
                <TrendingUp
                  className="h-4 w-4"
                  style={{ color: "var(--mc-info-icon)" }}
                />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(highestBalance)}
              </div>
              <div className="text-sm text-neutral-800 mt-1">
                {highestMonth}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Balance */}
        <Card
          className="h-full"
          style={{ borderLeft: "3px solid var(--mc-info-border)" }}
        >
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                Average Balance
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "var(--mc-info-bg)" }}
              >
                <Calendar
                  className="h-4 w-4"
                  style={{ color: "var(--mc-info-icon)" }}
                />
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(averageBalance)}
              </div>
              <div className="text-sm text-neutral-800 mt-1">
                12-month average
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Cash Flow */}
        <Card
          className="h-full"
          style={{ borderLeft: "3px solid var(--mc-info-border)" }}
        >
          <CardContent className="p-4 flex flex-col justify-between h-full gap-4">
            <div className="flex items-start justify-between gap-2">
              <span className="text-base font-medium text-neutral-800">
                Net Cash Flow
              </span>
              <div
                className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "var(--mc-info-bg)" }}
              >
                {netCashFlow >= 0 ? (
                  <TrendingUp
                    className="h-4 w-4"
                    style={{ color: "var(--mc-info-icon)" }}
                  />
                ) : (
                  <TrendingDown
                    className="h-4 w-4"
                    style={{ color: "var(--mc-info-icon)" }}
                  />
                )}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900 tracking-tight">
                {formatCurrency(netCashFlow)}
              </div>
              <div className="text-sm text-neutral-800 mt-1">
                {netCashFlow >= 0 ? "Positive" : "Negative"} over 12 months
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-4 px-4 py-3 border-b">
          <div className="flex items-center gap-1">
            <CardTitle className="text-lg font-semibold text-neutral-900">
              Cash Balance {getForecastTerm(true)}
            </CardTitle>
            <InfoTooltip
              title={`Cash Balance ${getForecastTerm(true)}`}
              content="Your projected monthly cash position over the next 12 months, based on expected income and outgoings from all connected data sources."
            />
          </div>
          <div className="flex items-center gap-4">
            <Tabs
              value={viewType}
              onValueChange={(v) => setViewType(v as ViewType)}
            >
              <TabsList>
                <TabsTrigger value="combined">Combined</TabsTrigger>
                <TabsTrigger value="line">Line</TabsTrigger>
                <TabsTrigger value="bar">Bar</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <CardContent>
          {/* Combined legend — above chart, matches 90-day style */}
          <div className="mb-3 flex flex-wrap items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-0.5 rounded-full"
                style={{ backgroundColor: CHART_LINE_COLOR }}
              />
              <span>Base {getForecastTerm(true)}</span>
            </div>
            {viewType === "combined" && (
              <>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-3 rounded-sm opacity-70"
                    style={{ backgroundColor: "var(--mc-success-icon)" }}
                  />
                  <span>{incomingTerm}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="w-4 h-3 rounded-sm opacity-70"
                    style={{ backgroundColor: "var(--mc-error-icon)" }}
                  />
                  <span>{outgoingTerm}</span>
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-0 border-t-2 border-dashed"
                style={{ borderColor: CHART_THRESHOLD_COLOR }}
              />
              <span>Threshold ({getCurrencySymbol()}50K)</span>
            </div>
          </div>

          <div
            className="relative h-[400px] w-full"
            style={{ cursor: "pointer" }}
          >
            <ResponsiveContainer width="100%" height="100%">
              {viewType === "combined" ? (
                <ComposedChart
                  data={baseData}
                  onClick={handleChartClick}
                  onMouseMove={handleChartMouseMove}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) =>
                      `${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />

                  {/* Threshold line */}
                  <ReferenceLine
                    y={threshold}
                    stroke={CHART_THRESHOLD_COLOR}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{
                      value: "Threshold",
                      position: "insideTopLeft",
                      fill: CHART_THRESHOLD_COLOR,
                      fontSize: 12,
                    }}
                  />

                  <Bar
                    dataKey="inflow"
                    fill="var(--mc-success-icon)"
                    name={incomingTerm}
                    opacity={0.5}
                    onClick={handleBarClick}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="outflow"
                    fill="var(--mc-error-icon)"
                    name={outgoingTerm}
                    opacity={0.5}
                    onClick={handleBarClick}
                    cursor="pointer"
                  />

                  <Line
                    type="monotone"
                    dataKey="balance"
                    name={`Base ${getForecastTerm(true)}`}
                    stroke={CHART_LINE_COLOR}
                    strokeWidth={3}
                    dot={false}
                    activeDot={{
                      r: 5,
                      fill: CHART_LINE_COLOR,
                      cursor: "pointer",
                    }}
                  />
                </ComposedChart>
              ) : viewType === "line" ? (
                <ComposedChart
                  data={baseData}
                  onClick={handleChartClick}
                  onMouseMove={handleChartMouseMove}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) =>
                      `${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />

                  <ReferenceLine
                    y={threshold}
                    stroke={CHART_THRESHOLD_COLOR}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{
                      value: "Threshold",
                      position: "insideTopLeft",
                      fill: CHART_THRESHOLD_COLOR,
                      fontSize: 12,
                    }}
                  />

                  <Line
                    type="monotone"
                    dataKey="balance"
                    name={`Base ${getForecastTerm(true)}`}
                    stroke={CHART_LINE_COLOR}
                    strokeWidth={3}
                    dot={false}
                    activeDot={{
                      r: 5,
                      fill: CHART_LINE_COLOR,
                      cursor: "pointer",
                    }}
                  />
                </ComposedChart>
              ) : (
                <BarChart
                  data={baseData}
                  onClick={handleChartClick}
                  onMouseMove={handleChartMouseMove}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) =>
                      `${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
                    }
                  />
                  <Tooltip content={<CustomTooltip />} />

                  <ReferenceLine
                    y={threshold}
                    stroke={CHART_THRESHOLD_COLOR}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    label={{
                      value: "Threshold",
                      position: "insideTopLeft",
                      fill: CHART_THRESHOLD_COLOR,
                      fontSize: 12,
                    }}
                  />

                  <Bar
                    dataKey="inflow"
                    fill="var(--mc-success-icon)"
                    name={incomingTerm}
                    onClick={handleBarClick}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="outflow"
                    fill="var(--mc-error-icon)"
                    name={outgoingTerm}
                    onClick={handleBarClick}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="balance"
                    name={`Base ${getForecastTerm(true)}`}
                    fill={CHART_LINE_COLOR}
                    opacity={0.8}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Balances / Category Balances / Transactions */}
      <Forecast12MonthTable
        tableRef={tableRef}
        historyResetKey={historyResetKey}
      />

      {/* Insights */}
      {mode === "demo" && (
        <Card>
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-1">
              <span className="text-lg font-semibold text-neutral-900">
                Key Insights
              </span>
              <InfoTooltip
                title="Key Insights"
                content="Automated observations about your 12-month cash position, including any projected threshold breaches."
              />
            </div>
          </div>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {lowestBalance < threshold && (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border"
                  style={{
                    backgroundColor: "var(--mc-error-bg)",
                    borderColor: "var(--mc-error-border)",
                  }}
                >
                  <AlertTriangle
                    className="h-5 w-5 mt-0.5"
                    style={{ color: "var(--mc-error-icon)" }}
                  />
                  <div>
                    <div
                      className="font-semibold"
                      style={{ color: "var(--mc-error-icon)" }}
                    >
                      Cash Flow Warning
                    </div>
                    <div
                      className="text-sm"
                      style={{ color: "var(--mc-error-icon)" }}
                    >
                      Your balance is projected to fall below the{" "}
                      {formatCurrency(threshold, "full")} threshold in{" "}
                      {lowestMonth}. Consider securing additional financing or
                      delaying non-essential payments.
                    </div>
                  </div>
                </div>
              )}

              {hasRecovery ? (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border"
                  style={{
                    backgroundColor: "var(--mc-info-bg)",
                    borderColor: "var(--mc-info-border)",
                  }}
                >
                  <TrendingUp
                    className="h-5 w-5 mt-0.5"
                    style={{ color: "var(--mc-info-icon)" }}
                  />
                  <div>
                    <div
                      className="font-semibold"
                      style={{ color: "var(--mc-info-icon)" }}
                    >
                      Recovery Expected After {troughMonth}
                    </div>
                    <div
                      className="text-sm"
                      style={{ color: "var(--mc-info-icon)" }}
                    >
                      Cash position improves after {troughMonth} as delayed
                      invoices are collected and new projects commence. Taking
                      action now on the AI recommendations can accelerate this
                      recovery.
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="flex items-start gap-3 p-3 rounded-lg border"
                  style={{
                    backgroundColor: "var(--mc-warning-bg)",
                    borderColor: "var(--mc-warning-border)",
                  }}
                >
                  <TrendingDown
                    className="h-5 w-5 mt-0.5"
                    style={{ color: "var(--mc-warning-icon)" }}
                  />
                  <div>
                    <div
                      className="font-semibold"
                      style={{ color: "var(--mc-warning-icon)" }}
                    >
                      {tailDeclining
                        ? `Cash Position Declining Since ${declineStartMonth}`
                        : `Cash Position Flat Since ${declineStartMonth}`}
                    </div>
                    <div
                      className="text-sm"
                      style={{ color: "var(--mc-warning-icon)" }}
                    >
                      No significant recovery is projected through the end of
                      the forecast period. Review the AI recommendations for
                      options to improve your cash position.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
